import { Plus, Trash2 } from 'lucide-react';
import { observer } from 'mobx-react';
import * as React from 'react';
import LocalizedTextArea from '@components/text-area/localized-text-area';
import { Alert, AlertDescription, AlertTitle } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Label } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import { injectTSDI } from '@core/tsdi';
import { ChallengeDataStore } from '@src/communities/community/challenges/challenge-form/data-store';
import { StepStatus } from '@src/communities/community/challenges/challenge-form/store';
import { ChallengeConsentFormType, ChallengeConsentsStore } from './store';

const SwitchField = ({
    id,
    label,
    checked,
    disabled,
    testId,
    onChange
}: {
    id: string;
    label: string;
    checked: boolean;
    disabled?: boolean;
    testId: string;
    onChange(checked: boolean): void;
}) => (
    <div className="flex flex-col gap-2">
        <Label htmlFor={id}>{label}</Label>
        <Switch
            id={id}
            checked={checked}
            onCheckedChange={onChange}
            data-testid={testId}
            disabled={disabled}
        />
    </div>
);

export const ConsentsStep = observer(() => {
    const { disabledConsentsEdit } = injectTSDI(ChallengeDataStore);
    const {
        fields: { consents },
        state,
        addConsent,
        removeConsent,
        validateConsents
    } = injectTSDI(ChallengeConsentsStore);

    if (state === StepStatus.DISABLED) {
        return (
            <Alert variant="warning">
                <AlertTitle>Consents are currently disabled</AlertTitle>
                <AlertDescription>
                    Auto-join is turned on, so consents can’t be defined.
                    Disable auto-join if you’d like to set up consents.
                </AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            {consents.value.map(
                (consentForm: ChallengeConsentFormType, index: number) => (
                    <div
                        key={index}
                        className="flex flex-col gap-4 rounded-lg border bg-card p-4"
                    >
                        <div className="flex items-center justify-between">
                            <p className="text-sm font-medium">
                                Consent #{index + 1}
                            </p>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-destructive"
                                disabled={disabledConsentsEdit}
                                onClick={() => removeConsent(index)}
                                aria-label="Remove consent"
                            >
                                <Trash2 />
                            </Button>
                        </div>
                        <LocalizedTextArea
                            label="Consent text"
                            value={consentForm.$.text.value}
                            onChange={(value) => {
                                consentForm.$.text.onChange(value);
                                validateConsents();
                            }}
                            disabled={disabledConsentsEdit}
                            error={consentForm.$.text.error}
                            required
                            data-testid={`consent-${index}-text-field"`}
                        />
                        <div className="grid gap-4 md:grid-cols-3">
                            <SwitchField
                                id={`consent-${index}-needs-checkbox`}
                                label="Needs checkbox"
                                checked={consentForm.$.needsCheckbox.value}
                                onChange={(checked) => {
                                    consentForm.$.needsCheckbox.onChange(
                                        checked
                                    );
                                    consentForm.$.required.onChange(checked);
                                }}
                                testId={`consent-${index}-needsCheckbox-switch"`}
                                disabled={disabledConsentsEdit}
                            />
                            <div>
                                {consentForm.$.needsCheckbox.value && (
                                    <SwitchField
                                        id={`consent-${index}-required`}
                                        label="Required"
                                        checked={consentForm.$.required.value}
                                        onChange={(checked) => {
                                            consentForm.$.required.onChange(
                                                checked
                                            );
                                            if (
                                                !checked &&
                                                consentForm.$.needsCheckbox
                                                    .value
                                            ) {
                                                consentForm.$.showAfterAccept.onChange(
                                                    false
                                                );
                                            }
                                        }}
                                        testId={`consent-${index}-required-switch"`}
                                        disabled={disabledConsentsEdit}
                                    />
                                )}
                            </div>
                            <SwitchField
                                id={`consent-${index}-show-after-accept`}
                                label="Show after accept"
                                checked={consentForm.$.showAfterAccept.value}
                                onChange={(checked) => {
                                    consentForm.$.showAfterAccept.onChange(
                                        checked
                                    );
                                }}
                                testId={`consent-${index}-showAfterAccept-switch"`}
                                disabled={
                                    disabledConsentsEdit ||
                                    !consentForm.$.required.value
                                }
                            />
                        </div>
                    </div>
                )
            )}

            <Button
                variant="outline"
                className="w-full border-2 border-dashed py-6"
                disabled={disabledConsentsEdit}
                onClick={addConsent}
                data-testid="add-consent-button"
            >
                <Plus />
                Add Consent
            </Button>

            {consents.value.length === 0 && (
                <div className="rounded-lg border-2 border-dashed bg-card p-6 text-center text-muted-foreground">
                    <p>No consents defined.</p>
                    <p className="text-sm">
                        Click "Add Consent" to define consents that will need to
                        be confirmed upon joining Challenge.
                    </p>
                </div>
            )}
        </div>
    );
});
